import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Box, Flex } from '@rover/kibble/core';
import { MQ } from '@rover/kibble/styles';
import ErrorBoundary from '@rover/react-lib/src/components/errorHandling/ErrorBoundary';
import { searchFiltersFactory } from '@rover/react-lib/src/factories/searchFiltersFactory';
import useIntersectionObserver from '@rover/react-lib/src/hooks/useIntersectionObserver';
import SearchResults from '@rover/react-lib/src/pages/search/SearchPage/components/SearchResults/SearchResults';
import {
  CountryConfiguration,
  OptimizedSearchResult,
  SearchCallout,
} from '@rover/rsdk/src/apiClient/latest';
import { DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE } from '@rover/shared/js/constants/service';
import { Callout, PriceTransparencyLevel, SearchScoreDebugData } from '@rover/types';
import type { SearchMapData } from '@rover/types/src/apiResponses/FetchSearchResponse';

import SearchResultsMap from './SearchResultsMap';

const FlexWithShadow = styled(Flex)`
  box-shadow: 0px 2px 12px -1px rgba(27, 31, 35, 0.24);

  ${MQ.SM_UP.toString()} {
    box-shadow: none;
  }
`;

type Props = {
  countryConfig: Pick<CountryConfiguration, 'urls' | 'bookingFeePercentage' | 'bookingFeeCap'>;
  searchResults?: OptimizedSearchResult[];
  language: string;
  preferCatReviews?: boolean;
  searchErrorMessage?: string;
  onClick?: (entityOpk: string, isFacility: boolean) => void;
  mapData: SearchMapData;
  mapSettings: React.ComponentProps<typeof SearchResultsMap>['settings'];
  priceTransparencyLevel: PriceTransparencyLevel;
  isSeoPage?: boolean;
  callouts?: SearchCallout[];
};

// wait for the map to be partially visible, or for the user to interact with the search layout,
// before swapping over to the dynamic map
const useDynamicMapTriggers = (
  mapContainerRef: Parameters<typeof useIntersectionObserver>[0]
): [boolean, (() => void) | undefined] => {
  // if we re-recreate this object each time we need it, it causes the `useEffect`
  // inside `useIntersectionObserver` to be invalidated on every render,
  // causing an infinite render loop - not good!
  const observerInit = useMemo(() => ({ threshold: 0.1 }), []);
  const isMapVisible = useIntersectionObserver(mapContainerRef, true, observerInit);

  // if the map isn't sufficiently on-screen, load it when the user interacts with the results
  const [hasInteractedWithSearchResults, setHasInteractedWithSearchResults] = useState(false);
  const shouldRenderDynamicMap = isMapVisible || hasInteractedWithSearchResults;

  // don't wire up event handlers if we've rendered the dynamic map
  const handleSearchResultsInteraction = shouldRenderDynamicMap
    ? undefined
    : (): void => setHasInteractedWithSearchResults(true);

  return [shouldRenderDynamicMap, handleSearchResultsInteraction];
};

const SearchResultsLayout: React.FC<Props> = ({
  countryConfig,
  searchResults = [],
  language,
  preferCatReviews,
  searchErrorMessage = '',
  onClick,
  mapData,
  mapSettings,
  priceTransparencyLevel,
  isSeoPage = false,
  callouts,
}) => {
  const compatibleSearchResults = searchResults.map((result) => ({
    ...result,
    // this casting is needed because there's no way of representing
    // tuples with different types in OpenAPI 3.0
    popoverDebugData: result.popoverDebugData as SearchScoreDebugData[],
  }));

  const [hoveringResultIndex, setHoveringResultIndex] = useState<number | null>(null);

  const searchResultRefs = useRef<HTMLDivElement[]>([]);

  const mapBoxRef = useRef<HTMLElement | null>(null);
  const [shouldRenderDynamicMap, onInteractWithSearchResults] = useDynamicMapTriggers(mapBoxRef);

  return (
    <FlexWithShadow
      width="100%"
      position="relative"
      onMouseOver={onInteractWithSearchResults}
      onFocus={onInteractWithSearchResults}
      onClick={onInteractWithSearchResults}
    >
      <ErrorBoundary location="SearchPageLayoutChildren">
        <Box flexBasis={['100%', '100%', '60%']} flexShrink="0">
          <SearchResults
            searchCallouts={callouts && callouts.map((callout) => callout as Callout)}
            onClick={onClick}
            hoveringResultIndex={hoveringResultIndex}
            alternateSearchResults={[]}
            currentFilters={searchFiltersFactory()}
            searchQueryDates={{ startDate: undefined, endDate: undefined }}
            searchQueryDistinctDates={{ dates: undefined }}
            alternateServiceName=""
            alternateServiceDescription=""
            didFiltersChangeSincePageLoad={false}
            isMapExpanded={false}
            language={language}
            memberProfileLinkParams={{}}
            searchErrorMessage={searchErrorMessage}
            searchResults={compatibleSearchResults}
            searchResponseNumberOfResults={compatibleSearchResults.length}
            isSeoPage={isSeoPage}
            onResultCardMouseEnter={setHoveringResultIndex}
            onResultCardMouseLeave={() => {
              setHoveringResultIndex(null);
            }}
            onFavoriteToggled={() => {}}
            preferCatReviews={preferCatReviews || false}
            searchCardRefs={(el, index) => (searchResultRefs.current[index] = el)}
            bookingFeePercentage={
              countryConfig.bookingFeePercentage || DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE
            }
            countryConfigUrls={countryConfig.urls}
            priceTransparencyLevel={priceTransparencyLevel}
          />
        </Box>
      </ErrorBoundary>
      <ErrorBoundary>
        <Box
          ref={mapBoxRef}
          flexBasis="40%"
          width="100%"
          height="100vh"
          position="sticky"
          top="0"
          display={['none', 'none', 'block']}
        >
          <SearchResultsMap
            center={{ lat: mapData?.center[0], lng: mapData?.center[1] }}
            zoomLevel={mapData.zoomlevel}
            settings={mapSettings}
            mapData={mapData}
            mapMode={shouldRenderDynamicMap ? 'dynamic' : 'static'}
            searchResults={compatibleSearchResults}
            searchResultsRefs={searchResultRefs.current}
            onMouseOver={(index) => setHoveringResultIndex(index)}
            onMouseOut={() => setHoveringResultIndex(null)}
            hoveringResultIndex={hoveringResultIndex}
            zIndex={(index) => (index === hoveringResultIndex ? 1 : 0)}
          />
        </Box>
      </ErrorBoundary>
    </FlexWithShadow>
  );
};

export default SearchResultsLayout;
